import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, Square, Loader2, Play, Pause, FileAudio, Brain, Sparkles, X, Check, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * VisitRecorder - Registra l'intera visita medico-paziente
 * Trascrive con IA e compila automaticamente la cartella clinica
 * 
 * Codice colori testo:
 * - ROSSO: Trascritto/compilato da IA
 * - NERO: Scritto manualmente dal medico
 * - VERDE: Selezionato/inserito dal paziente (anamnesi chatbot)
 */
export const VisitRecorder = ({ 
  patientId, 
  visitId,
  onTranscriptionComplete, 
  onAutoFill,
  currentEsame 
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcription, setTranscription] = useState('');
  const [aiSummary, setAiSummary] = useState(null);
  const [diagnosiSuggerite, setDiagnosiSuggerite] = useState([]);
  const [showTranscription, setShowTranscription] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Timer per durata registrazione
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isRecording, isPaused]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { 
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        } 
      });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, { 
        mimeType: 'audio/webm;codecs=opus' 
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        
        // Non fermare lo stream qui, lo facciamo dopo l'upload
      };

      mediaRecorder.start(1000); // Chunk ogni secondo
      setIsRecording(true);
      setRecordingTime(0);
      toast.info('🎤 Registrazione visita iniziata... La conversazione viene registrata');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Impossibile accedere al microfono. Verifica i permessi.');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        toast.info('▶️ Registrazione ripresa');
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
        toast.info('⏸️ Registrazione in pausa');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      // Ferma lo stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      toast.success('✅ Registrazione completata');
    }
  };

  const processAudio = async () => {
    if (!chunksRef.current.length) {
      toast.error('Nessuna registrazione da elaborare');
      return;
    }

    setIsProcessing(true);
    const token = localStorage.getItem('token');

    try {
      const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
      
      // Converti in base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        try {
          // Step 1: Trascrizione
          toast.info('🎯 Trascrizione in corso...');
          const transcribeRes = await axios.post(
            `${API}/api/visit/transcribe`,
            {
              audio_base64: base64Audio,
              audio_format: 'webm',
              patient_id: patientId,
              visit_id: visitId
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (transcribeRes.data.transcription) {
            setTranscription(transcribeRes.data.transcription);
            toast.success('✅ Trascrizione completata');
            
            // Step 2: Analisi IA e compilazione automatica
            toast.info('🧠 Analisi IA in corso...');
            const analyzeRes = await axios.post(
              `${API}/api/visit/analyze-transcription`,
              {
                transcription: transcribeRes.data.transcription,
                patient_id: patientId,
                current_esame: currentEsame
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (analyzeRes.data.summary) {
              setAiSummary(analyzeRes.data.summary);
              
              // Diagnosi suggerite con probabilità
              if (analyzeRes.data.diagnosi_suggerite) {
                setDiagnosiSuggerite(analyzeRes.data.diagnosi_suggerite);
              }
              
              // Callback per auto-compilazione
              if (onAutoFill && analyzeRes.data.auto_fill) {
                onAutoFill(analyzeRes.data.auto_fill, analyzeRes.data.diagnosi_suggerite);
                toast.success('✨ Campi compilati automaticamente dall\'IA');
              }
              
              if (onTranscriptionComplete) {
                onTranscriptionComplete(transcribeRes.data.transcription, analyzeRes.data.summary, analyzeRes.data.diagnosi_suggerite);
              }
            }
          }
        } catch (error) {
          console.error('Processing error:', error);
          toast.error(error.response?.data?.detail || 'Errore nell\'elaborazione');
        } finally {
          setIsProcessing(false);
        }
      };
    } catch (error) {
      console.error('Audio processing error:', error);
      toast.error('Errore elaborazione audio');
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border-2 border-purple-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
            <FileAudio className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-purple-800 text-sm">Registrazione Visita</h3>
            <p className="text-xs text-purple-600">Trascrive e compila automaticamente</p>
          </div>
        </div>
        
        {/* Timer */}
        {isRecording && (
          <div className={`px-3 py-1.5 rounded-lg font-mono text-lg font-bold ${
            isPaused ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600 animate-pulse'
          }`}>
            {formatTime(recordingTime)}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 mb-3">
        {!isRecording ? (
          <button
            onClick={startRecording}
            disabled={isProcessing}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 transition-all disabled:opacity-50"
          >
            <Mic className="w-5 h-5" />
            Inizia Registrazione Visita
          </button>
        ) : (
          <>
            <button
              onClick={pauseRecording}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all ${
                isPaused 
                  ? 'bg-green-500 text-white hover:bg-green-600' 
                  : 'bg-yellow-500 text-white hover:bg-yellow-600'
              }`}
            >
              {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
              {isPaused ? 'Riprendi' : 'Pausa'}
            </button>
            <button
              onClick={stopRecording}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white bg-gray-700 hover:bg-gray-800 transition-all"
            >
              <Square className="w-5 h-5" />
              Termina
            </button>
          </>
        )}
      </div>

      {/* Audio Preview & Process Button */}
      {audioUrl && !isRecording && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-white rounded-xl">
            <audio src={audioUrl} controls className="flex-1 h-10" />
          </div>
          
          <button
            onClick={processAudio}
            disabled={isProcessing}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 transition-all disabled:opacity-50"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Elaborazione IA in corso...
              </>
            ) : (
              <>
                <Brain className="w-5 h-5" />
                Trascrivi e Analizza con IA
              </>
            )}
          </button>
        </div>
      )}

      {/* Transcription Result */}
      {transcription && (
        <div className="mt-4 space-y-3">
          <button
            onClick={() => setShowTranscription(!showTranscription)}
            className="w-full flex items-center justify-between p-3 bg-white rounded-xl hover:bg-gray-50 transition-colors"
          >
            <span className="font-medium text-gray-700">📝 Trascrizione Completa</span>
            <span className="text-sm text-purple-600">{showTranscription ? 'Nascondi' : 'Mostra'}</span>
          </button>
          
          {showTranscription && (
            <div className="p-4 bg-white rounded-xl border border-purple-200 max-h-60 overflow-y-auto">
              <p className="text-sm text-gray-700 whitespace-pre-wrap" style={{ color: '#DC2626' }}>
                {transcription}
              </p>
              <p className="text-xs text-gray-400 mt-2 pt-2 border-t">
                🔴 Testo trascritto dall'IA
              </p>
            </div>
          )}
        </div>
      )}

      {/* AI Summary */}
      {aiSummary && (
        <div className="mt-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            <span className="font-semibold text-emerald-800 text-sm">Riassunto IA della Visita</span>
          </div>
          <p className="text-sm text-emerald-700">{aiSummary.summary}</p>
          
          {aiSummary.key_points && aiSummary.key_points.length > 0 && (
            <div className="mt-3 pt-3 border-t border-emerald-200">
              <p className="text-xs font-semibold text-emerald-800 mb-1">Punti chiave:</p>
              <ul className="text-xs text-emerald-700 space-y-1">
                {aiSummary.key_points.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-1">
                    <Check className="w-3 h-3 mt-0.5 text-emerald-500 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Diagnosi Suggerite con Probabilità */}
      {diagnosiSuggerite && diagnosiSuggerite.length > 0 && (
        <div className="mt-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-4 h-4 text-amber-600" />
            <span className="font-semibold text-amber-800 text-sm">🎯 Diagnosi Suggerite dall'IA</span>
          </div>
          <div className="space-y-2">
            {diagnosiSuggerite.sort((a, b) => b.probabilita - a.probabilita).map((diag, idx) => (
              <div 
                key={idx} 
                className="p-3 bg-white rounded-lg border border-amber-100 hover:border-amber-300 transition-colors cursor-pointer"
                onClick={() => {
                  if (onAutoFill) {
                    onAutoFill({ diagnosi: diag.diagnosi }, []);
                    toast.success(`Diagnosi "${diag.diagnosi}" applicata`);
                  }
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-800">{diag.diagnosi}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ 
                          width: `${diag.probabilita}%`,
                          backgroundColor: diag.probabilita >= 70 ? '#10B981' : 
                                          diag.probabilita >= 40 ? '#F59E0B' : '#EF4444'
                        }}
                      />
                    </div>
                    <span className={`text-sm font-bold ${
                      diag.probabilita >= 70 ? 'text-emerald-600' : 
                      diag.probabilita >= 40 ? 'text-amber-600' : 'text-red-500'
                    }`}>
                      {diag.probabilita}%
                    </span>
                  </div>
                </div>
                {diag.motivazione && (
                  <p className="text-xs text-gray-500 italic">{diag.motivazione}</p>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-amber-600 mt-2 italic">
            💡 Clicca su una diagnosi per applicarla alla cartella
          </p>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 pt-3 border-t border-purple-200">
        <p className="text-xs font-semibold text-gray-600 mb-2">Legenda colori nella cartella:</p>
        <div className="flex flex-wrap gap-3 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-red-500"></span>
            <span style={{ color: '#DC2626' }}>Trascritto IA</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-gray-800"></span>
            <span className="text-gray-800">Manuale medico</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
            <span style={{ color: '#059669' }}>Anamnesi paziente</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default VisitRecorder;
